import { IimDateRangeValue } from '@iim-angular/components';
import { ActivatedRouteSnapshot, Params } from '@angular/router';
import { UriBuilder } from 'uribuilder';
import { StorageKey, StorageService } from '@lib/core';
import { PurchaseRequisitionStatusEnum } from './PurchaseRequistionStatusEnum';
import { PurchaseRequisitionStatus } from './PurchaseRequisitionStatus';
import { SupplierCompany } from '@lib/supplier';
import dayjs from 'dayjs';
import { PersonExtended } from '@lib/user';
import { CustomerDepartment } from '@lib/client';
import { PurchaseRequisitionOrderStatus, PurchaseRequisitionDeliveryStatus } from '@lib/util';

/**
 * Query-string names of the purchase requisition list filter. They are identical to the request
 * parameters `apply(uri)` sends, so a shared link and the API call speak the same language
 * (EPROC-11936).
 */
export enum PurchaseRequisitionListFilterKey {
    DISPLAY_NUMBER = 'displayNumber',
    CONTENT = 'content',
    CREATED_FROM = 'createdFrom',
    CREATED_TO = 'createdTo',
    SUPPLIER = 'supplier',
    CONSUMER = 'consumer',
    DEPARTMENT_ID = 'departmentId',
    STATUS = 'status',
    ORDER_STATUS = 'orderStatus',
    DELIVERY_STATUS = 'deliveryStatus',
}

export class PurchaseRequisitionListFilter {
    /** Query param that discards the persisted state and opens the list with an empty filter. */
    public static readonly CLEAN_FILTER_KEY = 'clean';

    public static EMPTY(storage?: StorageService) {
        return new PurchaseRequisitionListFilter(
            '',
            '',
            undefined,
            undefined,
            undefined,
            new IimDateRangeValue(null, null),
            undefined,
            undefined,
            undefined,
            storage,
        );
    }

    /**
     * Rebuilds the filter for a list entry, following the precedence of the reference list
     * `order-list` (EPROC-11936): `?clean=true` wins and discards everything, otherwise query
     * params win as a whole, otherwise the session storage. The three sources are never merged -
     * a shared link must show exactly what its author saw, not a mix with the reader's own state.
     *
     * <p>The object filters (supplier, consumer, department) travel as ids. Resolving them needs
     * the option lists of the list component, so `RESTORE` leaves them empty and the component
     * fills them in; see `restoreObjectFiltersFromQueryParams()` there.</p>
     */
    public static async RESTORE(
        storage: StorageService,
        route?: ActivatedRouteSnapshot,
    ): Promise<PurchaseRequisitionListFilter> {
        if (route?.queryParams[PurchaseRequisitionListFilter.CLEAN_FILTER_KEY]) {
            return PurchaseRequisitionListFilter.EMPTY(storage);
        }

        if (route && PurchaseRequisitionListFilter.hasFilterInQueryParams(route)) {
            return PurchaseRequisitionListFilter.restoreFromQueryParams(storage, route.queryParams);
        }

        return PurchaseRequisitionListFilter.restoreFromStorage(storage);
    }

    /** True as soon as the route carries at least one of the filter values this list knows. */
    public static hasFilterInQueryParams(route: ActivatedRouteSnapshot): boolean {
        return Object.values(PurchaseRequisitionListFilterKey).some((key) => !!route.queryParams[key]);
    }

    private static restoreFromQueryParams(storage: StorageService, queryParams: Params): PurchaseRequisitionListFilter {
        const status = PurchaseRequisitionListFilter.enumParam(
            queryParams,
            PurchaseRequisitionListFilterKey.STATUS,
            PurchaseRequisitionStatusEnum,
        );

        return new PurchaseRequisitionListFilter(
            PurchaseRequisitionListFilter.textParam(queryParams, PurchaseRequisitionListFilterKey.DISPLAY_NUMBER),
            PurchaseRequisitionListFilter.textParam(queryParams, PurchaseRequisitionListFilterKey.CONTENT),
            undefined,
            undefined,
            undefined,
            new IimDateRangeValue(
                PurchaseRequisitionListFilter.dateParam(queryParams, PurchaseRequisitionListFilterKey.CREATED_FROM),
                PurchaseRequisitionListFilter.dateParam(queryParams, PurchaseRequisitionListFilterKey.CREATED_TO),
            ),
            status ? new PurchaseRequisitionStatus(status) : undefined,
            PurchaseRequisitionListFilter.enumParam(
                queryParams,
                PurchaseRequisitionListFilterKey.ORDER_STATUS,
                PurchaseRequisitionOrderStatus,
            ),
            PurchaseRequisitionListFilter.enumParam(
                queryParams,
                PurchaseRequisitionListFilterKey.DELIVERY_STATUS,
                PurchaseRequisitionDeliveryStatus,
            ),
            storage,
        );
    }

    /**
     * A query param can be repeated (`?content=a&content=b`), and then Angular hands over an array
     * instead of a string. Everything a shared link carries is user input, so each value is reduced to
     * the first string and anything else is treated as "not set" - a link nobody can hand-craft into a
     * broken list (EPROC-11936).
     */
    private static textParam(queryParams: Params, key: PurchaseRequisitionListFilterKey): string {
        const value: unknown = Array.isArray(queryParams[key]) ? queryParams[key][0] : queryParams[key];
        return typeof value === 'string' ? value : '';
    }

    /** An unparsable date is dropped instead of reaching the API, where it would end in a 500. */
    private static dateParam(queryParams: Params, key: PurchaseRequisitionListFilterKey): dayjs.Dayjs | null {
        const value = PurchaseRequisitionListFilter.textParam(queryParams, key);
        if (!value) {
            return null;
        }
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : null;
    }

    /** An unknown enum value is dropped instead of reaching the API, where it would end in a 400. */
    private static enumParam<T extends string>(
        queryParams: Params,
        key: PurchaseRequisitionListFilterKey,
        values: Record<string, T>,
    ): T | undefined {
        const value = PurchaseRequisitionListFilter.textParam(queryParams, key);
        return Object.values(values).find((candidate) => candidate === value);
    }

    private static async restoreFromStorage(storage: StorageService): Promise<PurchaseRequisitionListFilter> {
        if (await storage.has(StorageKey.PURCHASE_REQUISITION_LIST_FILTER)) {
            const filterStr: string | null = await storage.getItem(StorageKey.PURCHASE_REQUISITION_LIST_FILTER);
            if (filterStr) {
                const storedFilter: PurchaseRequisitionListFilterDto = JSON.parse(filterStr);
                return new PurchaseRequisitionListFilter(
                    storedFilter.displayNumber,
                    storedFilter.content,
                    storedFilter.supplier ? SupplierCompany.INSTANCE(storedFilter.supplier) : undefined,
                    storedFilter.consumer ? new PersonExtended(storedFilter.consumer) : undefined,
                    storedFilter.department ? CustomerDepartment.from(storedFilter.department) : undefined,
                    new IimDateRangeValue(
                        storedFilter.createdFrom ? dayjs(storedFilter.createdFrom) : null,
                        storedFilter.createdTo ? dayjs(storedFilter.createdTo) : null,
                    ),
                    storedFilter.status ? new PurchaseRequisitionStatus(storedFilter.status) : undefined,
                    storedFilter.orderStatus ?? undefined,
                    storedFilter.deliveryStatus ?? undefined,
                    storage,
                );
            }
        }

        return PurchaseRequisitionListFilter.EMPTY(storage);
    }

    public get supplierName(): string {
        return this._supplier?.valueDisplay ?? '';
    }

    public get supplier(): SupplierCompany | undefined {
        return this._supplier ? this._supplier : undefined;
    }

    public get consumerName(): string {
        return this._consumer ? this._consumer?.fullName : '';
    }

    public get consumer(): PersonExtended | undefined {
        return this._consumer ? this._consumer : undefined;
    }

    public get department(): CustomerDepartment | undefined {
        return this._department ? this._department : undefined;
    }

    public get departmentName(): string {
        return this._department ? this._department.name : '';
    }

    constructor(
        private _displayNumber = '',
        private _content = '',
        private _supplier?: SupplierCompany,
        private _consumer?: PersonExtended,
        private _department?: CustomerDepartment,
        private _created: IimDateRangeValue | null = new IimDateRangeValue(),
        private _status: PurchaseRequisitionStatus | undefined = undefined,
        private _orderStatus: PurchaseRequisitionOrderStatus | undefined = undefined,
        private _deliveryStatus: PurchaseRequisitionDeliveryStatus | undefined = undefined,
        private readonly storage?: StorageService,
    ) {
        this._displayNumber = PurchaseRequisitionListFilter.normalizeText(this._displayNumber);
        this._content = PurchaseRequisitionListFilter.normalizeText(this._content);
        this.save();
    }

    /** Called from the constructor as well, because restoring writes past the setters. */
    private static normalizeText(value: string | undefined): string {
        return typeof value === 'string' ? value.trim() : '';
    }

    public get displayNumber(): string {
        return this._displayNumber;
    }

    /**
     * Copying the number by text selection appends a blank, and the server compares it with
     * `SUBSTR(pr.id, 31) LIKE CONCAT(?, '%')`, where a blank is part of the pattern and finds nothing
     * (EPROC-11936). The number is the last six characters of the requisition UUID - pure hex, never a
     * blank - so trimming cannot lose a valid input, and writing the trimmed value back into the
     * visible field through the two-way binding of the shared filter input is wanted here.
     *
     * <p>The `content` setter deliberately does not trim: it binds two-way as well, so it would write
     * back while the user is typing and swallow the blank just typed between two words.</p>
     */
    set displayNumber(value: string) {
        this._displayNumber = PurchaseRequisitionListFilter.normalizeText(value);
        this.save();
    }

    public get content(): string {
        return this._content;
    }

    set content(value: string) {
        this._content = value;
        this.save();
    }

    public get created(): IimDateRangeValue | null {
        return this._created;
    }

    public set created(range: IimDateRangeValue | null) {
        this._created = range;
        this.save();
    }

    public removeCreated() {
        this._created = null;
        this.save();
    }

    public setSupplier(supplier: SupplierCompany): void {
        this._supplier = supplier;
        this.save();
    }

    public removeSupplier(): void {
        this._supplier = undefined;
        this.save();
    }

    public setConsumer(consumer: PersonExtended): void {
        this._consumer = consumer;
        this.save();
    }

    public setDepartment(department: CustomerDepartment): void {
        this._department = department;
        this.save();
    }

    public removeConsumer(): void {
        this._consumer = undefined;
        this.save();
    }

    public removeDepartment(): void {
        this._department = undefined;
        this.save();
    }

    get status(): PurchaseRequisitionStatusEnum | undefined {
        return this._status ? this._status?.value : undefined;
    }

    set status(value: PurchaseRequisitionStatusEnum | undefined) {
        this._status = value ? new PurchaseRequisitionStatus(value) : undefined;
        this.save();
    }

    get orderStatus(): PurchaseRequisitionOrderStatus | undefined {
        return this._orderStatus;
    }

    set orderStatus(value: PurchaseRequisitionOrderStatus | undefined) {
        this._orderStatus = value;
        this.save();
    }

    get deliveryStatus(): PurchaseRequisitionDeliveryStatus | undefined {
        return this._deliveryStatus;
    }

    set deliveryStatus(value: PurchaseRequisitionDeliveryStatus | undefined) {
        this._deliveryStatus = value;
        this.save();
    }

    /**
     * The filter as query params for the list URL, so a filtered list can be shared and reloaded
     * (EPROC-11936). Empty values are left out; the object filters travel as their id, exactly as
     * `apply(uri)` sends them to the API.
     */
    public get filterAsParams(): Params {
        const params: Params = {};

        if (this._displayNumber) {
            params[PurchaseRequisitionListFilterKey.DISPLAY_NUMBER] = this._displayNumber;
        }
        if (this._content) {
            params[PurchaseRequisitionListFilterKey.CONTENT] = this._content;
        }
        if (this._created?.from) {
            params[PurchaseRequisitionListFilterKey.CREATED_FROM] = this._created.from.format();
        }
        if (this._created?.to) {
            params[PurchaseRequisitionListFilterKey.CREATED_TO] = this._created.to.format();
        }
        if (this._supplier) {
            params[PurchaseRequisitionListFilterKey.SUPPLIER] = this._supplier.id;
        }
        if (this._consumer) {
            params[PurchaseRequisitionListFilterKey.CONSUMER] = this._consumer.id;
        }
        if (this._department) {
            params[PurchaseRequisitionListFilterKey.DEPARTMENT_ID] = this._department.id;
        }
        if (this._status) {
            params[PurchaseRequisitionListFilterKey.STATUS] = this._status.value;
        }
        if (this._orderStatus) {
            params[PurchaseRequisitionListFilterKey.ORDER_STATUS] = this._orderStatus;
        }
        if (this._deliveryStatus) {
            params[PurchaseRequisitionListFilterKey.DELIVERY_STATUS] = this._deliveryStatus;
        }

        return params;
    }

    /**
     * Adds the active filters to the request URI. Same values as `filterAsParams`, by construction:
     * the request parameters of the API and the query params of the list URL are deliberately the same
     * names, so a shared link and the request it produces cannot drift apart (EPROC-11936).
     */
    public apply(uri: UriBuilder): void {
        Object.assign(uri.query, this.filterAsParams);
    }

    private save(): void {
        if (this.storage) {
            this.storage.setItem(
                StorageKey.PURCHASE_REQUISITION_LIST_FILTER,
                new PurchaseRequisitionListFilterDto(
                    this.displayNumber,
                    this.content,
                    this._created?.from?.format(),
                    this._created?.to?.format(),
                    this._supplier,
                    this._consumer,
                    this._department,
                    this._status?.value,
                    this._orderStatus,
                    this._deliveryStatus,
                ).toString(),
            );
        }
    }
}

export class PurchaseRequisitionListFilterDto {
    constructor(
        public readonly displayNumber: string,
        public readonly content: string,
        public readonly createdFrom?: string,
        public readonly createdTo?: string,
        public readonly supplier?: SupplierCompany,
        public readonly consumer?: PersonExtended,
        public readonly department?: CustomerDepartment,
        public readonly status?: PurchaseRequisitionStatusEnum,
        public readonly orderStatus?: PurchaseRequisitionOrderStatus,
        public readonly deliveryStatus?: PurchaseRequisitionDeliveryStatus,
    ) {}

    public toString(): string {
        return JSON.stringify(this);
    }
}
